import { SystemLayerKey } from '@packages/database/types';
import { FEATURE_REGISTRY } from '../config/featureRegistry';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { useEffect } from 'react';
import { useUniqueTicket } from '@/hooks/api/useTickets';
import { useMapZoom } from '../hooks/useMapZoom';
import { useDialogManager } from '@/hooks/useDialogManager';
import { useHighlightLayer } from '../hooks/useHighlightLayer';
import { useSelectionStore } from '@/stores/selectionStore';
import { useSearchParams } from 'react-router';
import { useMap } from '@/providers/MapProvider';

export const FeatureSelectionManager = () => {
  const { map } = useMap();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selected, activeIndex, source, setSelection, clear } =
    useSelectionStore();

  const { highlightFeature, clearHighlight } = useHighlightLayer();
  const { openDialog } = useDialogManager();
  const { zoomToFeature } = useMapZoom();

  const currentItem = selected[activeIndex];
  const ticketIdFromUrl = searchParams.get('ticketId') ?? '';
  const { data: ticket } = useUniqueTicket(ticketIdFromUrl);

  // Sync URL -> Store
  useEffect(() => {
    if (
      ticketIdFromUrl &&
      !selected.some((s) => s.id === ticketIdFromUrl) &&
      ticket
    ) {
      setSelection(
        [{ id: ticket.id, data: ticket, layerKey: SystemLayerKey.TICKET }],
        'url',
      );
    } else if (!ticketIdFromUrl && source === 'url' && selected.length > 0) {
      clear();
    }
  }, [ticketIdFromUrl, ticket, selected.length]);

  // Highlighting & Zooming Logic
  useEffect(() => {
    if (!map) {
      clearHighlight();
      return;
    }

    const olFeatures: Feature[] = [];

    selected.forEach((item) => {
      if (item.feature) {
        olFeatures.push(item.feature);
      } else {
        map
          .getLayers()
          .getArray()
          .forEach((layer) => {
            if (layer instanceof VectorLayer) {
              const f = layer.getSource()?.getFeatureById(item.id);
              if (f) olFeatures.push(f);
            }
          });
      }
    });

    highlightFeature(olFeatures);

    if (olFeatures.length === 1) {
      const config = FEATURE_REGISTRY[selected[0].layerKey];
      if (config?.shouldZoom || source === 'search') {
        zoomToFeature(olFeatures[0]);
      }
    }
  }, [selected, map, source, ticketIdFromUrl]);

  useEffect(() => {
    if (selected.length === 1 && source !== 'url') {
      const item = selected[0];
      if (
        item.layerKey === SystemLayerKey.TICKET &&
        ticketIdFromUrl !== item.id
      ) {
        setSearchParams(
          (prev) => {
            const p = new URLSearchParams(prev);
            p.set('ticketId', item.id);
            return p;
          },
          { replace: true },
        );
      }
    }
  }, [selected, source, ticketIdFromUrl]);

  useEffect(() => {
    if (selected.length === 1 && source !== 'url' && currentItem) {
      const entry = FEATURE_REGISTRY[currentItem.layerKey];
      const isAlreadyOpen =
        searchParams.get('dialog') === 'edit-ticket' &&
        searchParams.get('ticketId') === currentItem.id;

      if (entry?.presentation === 'form' && !isAlreadyOpen) {
        openDialog('edit-ticket', currentItem.id);
      }
    }
  }, [selected, source, currentItem, searchParams]);

  return null;
};
